import { Injectable, signal, WritableSignal } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'erpclass-help-theme';
const EVENT_NAME = 'erpclass-help-colorScheme';

/**
 * Minimal replacement for @coreui/angular's ColorModeService.
 * Keeps the same localStorage key and event name so existing sessions
 * (persisted before the CoreUI removal) don't lose their saved theme.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  public readonly colorMode: WritableSignal<ThemeMode>;

  constructor() {
    this.colorMode = signal<ThemeMode>(this.readStoredMode());
    this.applyMode(this.colorMode());
  }

  public setColorMode(mode: ThemeMode): void {
    this.colorMode.set(mode);
    this.persistMode(mode);
    this.applyMode(mode);
  }

  public toggle(): void {
    this.setColorMode(this.resolvedMode() === 'dark' ? 'light' : 'dark');
  }

  public resolvedMode(): 'light' | 'dark' {
    const mode: ThemeMode = this.colorMode();
    if (mode === 'light' || mode === 'dark') {
      return mode;
    }
    return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private readStoredMode(): ThemeMode {
    if (typeof localStorage === 'undefined') {
      return 'dark';
    }
    const stored: string | null = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'auto' ? stored : 'dark';
  }

  private persistMode(mode: ThemeMode): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(STORAGE_KEY, mode);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: mode }));
    }
  }

  private applyMode(mode: ThemeMode): void {
    if (typeof document === 'undefined') {
      return;
    }
    const resolved: 'light' | 'dark' = mode === 'auto' ? this.resolvedMode() : mode;
    document.documentElement.dataset['theme'] = resolved;
  }
}
